import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Avatar,
  TextField, InputAdornment, IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import {
  FamilyRestroom as JuniorIcon,
  Search as SearchIcon,
  Lock as BlockIcon,
  LockOpen as UnblockIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface JuniorConta {
  id: number;
  pai_user_id: string;
  teen_user_id: string | null;
  teen_nome: string;
  limite_diario: number;
  gasto_hoje: number;
  ativo: number;
  notificacoes: number;
  estado_convite: string;
  created_at: string;
}

const fmt = (v: number) => `${v.toLocaleString('pt-CV')} ECV`;

const JuniorAdminPage: React.FC = () => {
  const [contas, setContas]   = useState<JuniorConta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [search, setSearch]   = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/junior/accounts')
      .then(r => setContas(r.data.data || []))
      .catch(() => setError('Erro ao carregar contas Junior'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (conta: JuniorConta) => {
    await api.put(`/admin/junior/accounts/${conta.id}/toggle`);
    load();
  };

  const filtered = contas.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.teen_nome.toLowerCase().includes(q) ||
      (c.teen_user_id || '').toLowerCase().includes(q);
  });

  const ativos = contas.filter(c => c.ativo && c.estado_convite === 'ACEITE').length;
  const pendentes = contas.filter(c => c.estado_convite === 'PENDENTE').length;
  const totalGastoHoje = contas.reduce((s, c) => s + Number(c.gasto_hoje), 0);

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: '#6B2FA0', width: 48, height: 48 }}><JuniorIcon /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pagali Junior</Typography>
          <Typography variant="body2" color="text.secondary">
            Contas de jovens com controlo parental activo
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Contas activas',  value: ativos,             color: '#10B981' },
          { label: 'Convites pendentes', value: pendentes,        color: '#F59E0B' },
          { label: 'Gasto hoje (ECV)', value: fmt(totalGastoHoje), color: '#6B2FA0', isText: true },
        ].map(s => (
          <Paper key={s.label} sx={{ p: 2, minWidth: 160, flex: 1 }}>
            <Typography variant="h4" fontWeight={700} color={s.color}>
              {s.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      <TextField size="small" placeholder="Pesquisar por nome ou user_id..."
        value={search} onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: 320 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><b>Jovem</b></TableCell>
                <TableCell><b>Estado</b></TableCell>
                <TableCell align="right"><b>Limite/dia</b></TableCell>
                <TableCell><b>Gasto hoje</b></TableCell>
                <TableCell align="center"><b>Notif.</b></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma conta Junior encontrada
                  </TableCell>
                </TableRow>
              ) : filtered.map(conta => {
                const pct = conta.limite_diario > 0
                  ? Math.min(conta.gasto_hoje / conta.limite_diario, 1)
                  : 0;
                return (
                  <TableRow key={conta.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{conta.teen_nome}</Typography>
                      {conta.teen_user_id && (
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {conta.teen_user_id.substring(0, 12)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          conta.estado_convite === 'PENDENTE' ? 'Pendente' :
                          conta.ativo ? 'Activo' : 'Bloqueado'
                        }
                        size="small"
                        color={
                          conta.estado_convite === 'PENDENTE' ? 'warning' :
                          conta.ativo ? 'success' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">{fmt(Number(conta.limite_diario))}</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Typography variant="caption">{fmt(Number(conta.gasto_hoje))}</Typography>
                      <LinearProgress variant="determinate" value={pct * 100}
                        sx={{ mt: 0.5, borderRadius: 1, height: 4,
                          '& .MuiLinearProgress-bar': { bgcolor: pct > 0.8 ? 'error.main' : '#6B2FA0' } }} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={conta.notificacoes ? 'On' : 'Off'}
                        size="small"
                        color={conta.notificacoes ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {conta.estado_convite === 'ACEITE' && (
                        <Tooltip title={conta.ativo ? 'Bloquear' : 'Desbloquear'}>
                          <IconButton size="small" onClick={() => handleToggle(conta)}>
                            {conta.ativo
                              ? <BlockIcon fontSize="small" color="warning" />
                              : <UnblockIcon fontSize="small" color="success" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default JuniorAdminPage;
